package or.garden.manager.features.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.components.SnackbarType
import or.garden.manager.domain.CropService
import or.garden.manager.domain.model.CropData
import or.garden.manager.domain.model.CropRequest
import or.garden.manager.domain.model.CropTranslationRequest
import or.garden.manager.domain.model.NutrientStage
import or.garden.manager.domain.model.TransplantWindow
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

@Composable
fun AdminCropEditScreen(
    cropId: Int?,
    onBack: () -> Unit,
    showSnackbar: (String, SnackbarType) -> Unit = { _, _ -> }
) {
    var isLoading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var isGeneratingAI by remember { mutableStateOf(false) }
    var allCrops by remember { mutableStateOf<List<CropData>>(emptyList()) }

    // Translation data
    var bsName by remember { mutableStateOf("") }
    var bsLatinName by remember { mutableStateOf("") }
    var bsDescription by remember { mutableStateOf("") }

    var enName by remember { mutableStateOf("") }
    var enLatinName by remember { mutableStateOf("") }
    var enDescription by remember { mutableStateOf("") }

    var icon by remember { mutableStateOf("") }
    var spacingPlants by remember { mutableStateOf("20") }
    var spacingRows by remember { mutableStateOf("30") }
    var height by remember { mutableStateOf("") }
    var minTemp by remember { mutableStateOf("") }
    var optTempMin by remember { mutableStateOf("") }
    var optTempMax by remember { mutableStateOf("") }
    var isCompanion by remember { mutableStateOf(false) }

    var resourceConsumption by remember { mutableStateOf("") }
    var waterRequirements by remember { mutableStateOf("") }
    var frostSensitivity by remember { mutableStateOf("") }

    var selectedGoodCompanionIds by remember { mutableStateOf(setOf<Int>()) }
    var selectedBadCompanionIds by remember { mutableStateOf(setOf<Int>()) }

    // Nutrient Stages
    var nutrients by remember {
        mutableStateOf(
            listOf(
                NutrientStage(
                    weekStart = null,
                    weekEnd = null,
                    phaseName = "transplanting",
                    nitrogen = null,
                    phosphorus = null,
                    potassium = null
                ),
                NutrientStage(
                    weekStart = null,
                    weekEnd = null,
                    phaseName = "flowering",
                    nitrogen = null,
                    phosphorus = null,
                    potassium = null
                ),
                NutrientStage(
                    weekStart = null,
                    weekEnd = null,
                    phaseName = "fruiting",
                    nitrogen = null,
                    phosphorus = null,
                    potassium = null
                )
            )
        )
    }

    // Transplant Schedule
    var contStart by remember { mutableStateOf("") }
    var contEnd by remember { mutableStateOf("") }
    var contNotesBS by remember { mutableStateOf("") }
    var contNotesEN by remember { mutableStateOf("") }
    var medStart by remember { mutableStateOf("") }
    var medEnd by remember { mutableStateOf("") }
    var medNotesBS by remember { mutableStateOf("") }
    var medNotesEN by remember { mutableStateOf("") }
    var mountStart by remember { mutableStateOf("") }
    var mountEnd by remember { mutableStateOf("") }
    var mountNotesBS by remember { mutableStateOf("") }
    var mountNotesEN by remember { mutableStateOf("") }

    fun loadAllCrops() {
        CropService.getCrops(null) { state ->
            if (state is ResultState.Success) {
                allCrops = state.data.data ?: emptyList()
            }
        }
    }

    fun loadCropData() {
        if (cropId == null) return
        isLoading = true
        CropService.getCropFull(cropId) { state ->
            when (state) {
                is ResultState.Success -> {
                    val data = state.data.data
                    if (data != null) {
                        data.translations.find { it.languageCode == "bs" }?.let {
                            bsName = it.name
                            bsLatinName = it.latinName ?: ""
                            bsDescription = it.description ?: ""
                        }
                        data.translations.find { it.languageCode == "en" }?.let {
                            enName = it.name
                            enLatinName = it.latinName ?: ""
                            enDescription = it.description ?: ""
                        }
                        icon = data.icon ?: ""
                        spacingPlants = data.spacingBetweenPlants.toString()
                        spacingRows = data.spacingBetweenRows.toString()
                        height = data.height?.toString() ?: ""
                        minTemp = data.minTemperature?.toString() ?: ""
                        optTempMin = data.optimalTemperatureMin?.toString() ?: ""
                        optTempMax = data.optimalTemperatureMax?.toString() ?: ""
                        isCompanion = data.isCompanion

                        resourceConsumption = data.resourceConsumption ?: ""
                        waterRequirements = data.waterRequirements ?: ""
                        frostSensitivity = data.frostSensitivity ?: ""

                        selectedGoodCompanionIds = data.goodCompanions.map { it.id }.toSet()
                        selectedBadCompanionIds = data.badCompanions.map { it.id }.toSet()

                        // Sync existing nutrients or keep defaults
                        val existingNutrients = data.nutrients ?: emptyList()
                        nutrients = listOf("transplanting", "flowering", "fruiting").map { phase ->
                            existingNutrients.find { it.phaseName?.lowercase() == phase }
                                ?: NutrientStage(null, null, phase, null, null, null)
                        }

                        data.transplantSchedule.forEach { (key, window) ->
                            when (key) {
                                "continental" -> {
                                    contStart = window.startMonth?.toString() ?: ""
                                    contEnd = window.endMonth?.toString() ?: ""
                                    contNotesBS = window.notesBS ?: ""
                                    contNotesEN = window.notesEN ?: ""
                                }

                                "mediterranean" -> {
                                    medStart = window.startMonth?.toString() ?: ""
                                    medEnd = window.endMonth?.toString() ?: ""
                                    medNotesBS = window.notesBS ?: ""
                                    medNotesEN = window.notesEN ?: ""
                                }

                                "mountain" -> {
                                    mountStart = window.startMonth?.toString() ?: ""
                                    mountEnd = window.endMonth?.toString() ?: ""
                                    mountNotesBS = window.notesBS ?: ""
                                    mountNotesEN = window.notesEN ?: ""
                                }
                            }
                        }
                    }
                    isLoading = false
                }

                is ResultState.Error -> {
                    showSnackbar("Greška pri učitavanju: ${state.message}", SnackbarType.ERROR)
                    isLoading = false
                }

                else -> {}
            }
        }
    }

    LaunchedEffect(cropId) {
        loadAllCrops()
        loadCropData()
    }

    fun String.toMonthName(): String? {
        val months = listOf(
            "Januar", "Februar", "Mart", "April", "Maj", "Juni",
            "Juli", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"
        )
        return this.toIntOrNull()?.let { months.getOrNull(it - 1) }
    }

    fun handleSave() {
        if (bsName.isBlank()) {
            showSnackbar("Ime kulture je obavezno.", SnackbarType.WARNING)
            return
        }

        isSaving = true
        val scheduleMap = mutableMapOf<String, TransplantWindow>()
        if (contStart.isNotEmpty() || contEnd.isNotEmpty()) {
            scheduleMap["continental"] = TransplantWindow(
                startMonth = contStart.toIntOrNull(),
                endMonth = contEnd.toIntOrNull(),
                startMonthName = contStart.toMonthName(),
                endMonthName = contEnd.toMonthName(),
                notesBS = contNotesBS.ifEmpty { null },
                notesEN = contNotesEN.ifEmpty { null }
            )
        }
        if (medStart.isNotEmpty() || medEnd.isNotEmpty()) {
            scheduleMap["mediterranean"] = TransplantWindow(
                startMonth = medStart.toIntOrNull(),
                endMonth = medEnd.toIntOrNull(),
                startMonthName = medStart.toMonthName(),
                endMonthName = medEnd.toMonthName(),
                notesBS = medNotesBS.ifEmpty { null },
                notesEN = medNotesEN.ifEmpty { null }
            )
        }
        if (mountStart.isNotEmpty() || mountEnd.isNotEmpty()) {
            scheduleMap["mountain"] = TransplantWindow(
                startMonth = mountStart.toIntOrNull(),
                endMonth = mountEnd.toIntOrNull(),
                startMonthName = mountStart.toMonthName(),
                endMonthName = mountEnd.toMonthName(),
                notesBS = mountNotesBS.ifEmpty { null },
                notesEN = mountNotesEN.ifEmpty { null }
            )
        }

        val request = CropRequest(
            icon = icon,
            spacingBetweenPlants = spacingPlants.toIntOrNull() ?: 20,
            spacingBetweenRows = spacingRows.toIntOrNull() ?: 30,
            height = height.toIntOrNull(),
            minTemperature = minTemp.toIntOrNull(),
            optimalTemperatureMin = optTempMin.toIntOrNull(),
            optimalTemperatureMax = optTempMax.toIntOrNull(),
            isCompanion = isCompanion,
            resourceConsumption = resourceConsumption.ifEmpty { null },
            waterRequirements = waterRequirements.ifEmpty { null },
            frostSensitivity = frostSensitivity.ifEmpty { null },
            translations = listOf(
                CropTranslationRequest(
                    languageCode = "bs",
                    name = bsName,
                    latinName = bsLatinName,
                    description = bsDescription
                ),
                CropTranslationRequest(
                    languageCode = "en",
                    name = enName,
                    latinName = enLatinName,
                    description = enDescription
                )
            ),
            goodCompanionIds = selectedGoodCompanionIds.toList(),
            badCompanionIds = selectedBadCompanionIds.toList(),
            nutrients = nutrients.filter { !it.phaseName.isNullOrEmpty() },
            transplantSchedule = scheduleMap
        )

        val resultHandler: (ResultState<*>) -> Unit = { state ->
            when (state) {
                is ResultState.Success -> {
                    showSnackbar("Kultura uspješno spremljena!", SnackbarType.SUCCESS)
                    isSaving = false
                    onBack()
                }

                is ResultState.Error -> {
                    showSnackbar("Greška pri spremanju: ${state.message}", SnackbarType.ERROR)
                    isSaving = false
                }

                else -> {}
            }
        }

        if (cropId == null) {
            CropService.createCrop(request) { resultHandler(it) }
        } else {
            CropService.updateCrop(cropId, request) { resultHandler(it) }
        }
    }

    fun handleFillWithAI() {
        if (bsName.isBlank()) {
            showSnackbar("Unesite ime kulture prije generisanja podataka.", SnackbarType.WARNING)
            return
        }
        isGeneratingAI = true
        CropService.fillWithAI(bsName) { state ->
            when (state) {
                is ResultState.Success -> {
                    val data = state.data.data
                    if (data != null) {
                        // Populate fields from AI response
                        data.translations.find { it.languageCode == "bs" }?.let {
                            bsLatinName = it.latinName ?: ""
                            bsDescription = it.description ?: ""
                        }
                        data.translations.find { it.languageCode == "en" }?.let {
                            enName = it.name
                            enLatinName = it.latinName ?: ""
                            enDescription = it.description ?: ""
                        }
                        icon = data.icon ?: ""
                        spacingPlants = data.spacingBetweenPlants?.toString() ?: spacingPlants
                        spacingRows = data.spacingBetweenRows?.toString() ?: spacingRows
                        height = data.height?.toString() ?: ""
                        minTemp = data.minTemperature?.toString() ?: ""
                        optTempMin = data.optimalTemperatureMin?.toString() ?: ""
                        optTempMax = data.optimalTemperatureMax?.toString() ?: ""
                        isCompanion = data.isCompanion

                        resourceConsumption = data.resourceConsumption ?: ""
                        waterRequirements = data.waterRequirements ?: ""
                        frostSensitivity = data.frostSensitivity ?: ""

                        selectedGoodCompanionIds = data.goodCompanions.map { it.id }.toSet()
                        selectedBadCompanionIds = data.badCompanions.map { it.id }.toSet()

                        val existingNutrients = data.nutrients ?: emptyList()
                        nutrients = listOf("transplanting", "flowering", "fruiting").map { phase ->
                            existingNutrients.find { it.phaseName?.lowercase() == phase }
                                ?: NutrientStage(null, null, phase, null, null, null)
                        }

                        data.transplantSchedule.forEach { (key, window) ->
                            when (key) {
                                "continental" -> {
                                    contStart = window.startMonth?.toString() ?: ""
                                    contEnd = window.endMonth?.toString() ?: ""
                                    contNotesBS = window.notesBS ?: ""
                                    contNotesEN = window.notesEN ?: ""
                                }

                                "mediterranean" -> {
                                    medStart = window.startMonth?.toString() ?: ""
                                    medEnd = window.endMonth?.toString() ?: ""
                                    medNotesBS = window.notesBS ?: ""
                                    medNotesEN = window.notesEN ?: ""
                                }

                                "mountain" -> {
                                    mountStart = window.startMonth?.toString() ?: ""
                                    mountEnd = window.endMonth?.toString() ?: ""
                                    mountNotesBS = window.notesBS ?: ""
                                    mountNotesEN = window.notesEN ?: ""
                                }
                            }
                        }
                        showSnackbar("Podaci uspješno popunjeni pomoću AI!", SnackbarType.SUCCESS)
                    }
                    isGeneratingAI = false
                }

                is ResultState.Error -> {
                    showSnackbar("Greška pri AI generisanju: ${state.message}", SnackbarType.ERROR)
                    isGeneratingAI = false
                }

                else -> {}
            }
        }
    }

    Div({ classes("max-w-4xl", "mx-auto", "pb-20") }) {
        // Header
        Div({ classes("flex", "items-center", "justify-between", "mb-8") }) {
            Div({ classes("flex", "items-center", "gap-4") }) {
                Button({
                    classes("p-2", "text-gray-500", "hover:text-emerald-600", "transition-colors")
                    onClick { onBack() }
                }) {
                    I({ classes("fas", "fa-arrow-left", "text-xl") })
                }
                H1({ classes("text-2xl", "font-bold", "text-gray-900") }) {
                    Text(if (cropId == null) "Dodaj novu kulturu" else "Uredi kulturu")
                }
            }

            Div({ classes("flex", "items-center", "gap-3") }) {
                Button({
                    classes(
                        "bg-blue-600",
                        "text-white",
                        "p-3",
                        "rounded-xl",
                        "hover:bg-blue-700",
                        "transition-colors",
                        "font-bold",
                        "flex",
                        "items-center",
                        "justify-center"
                    )
                    if (isGeneratingAI || bsName.isBlank()) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick { handleFillWithAI() }
                    title("Popuni sa AI")
                }) {
                    if (isGeneratingAI) {
                        I({ classes("fas", "fa-spinner", "fa-spin", "text-xl") })
                    } else {
                        val aiIcon = getCropImageSource("ai")
                        if (aiIcon != null) {
                            Img(src = aiIcon, attrs = { classes("w-6", "h-6", "object-contain") })
                        } else {
                            I({ classes("fas", "fa-magic", "text-xl") })
                        }
                    }
                }

                Button({
                    classes(
                        "bg-emerald-600",
                        "text-white",
                        "p-3",
                        "rounded-xl",
                        "hover:bg-emerald-700",
                        "transition-colors",
                        "font-bold",
                        "flex",
                        "items-center",
                        "justify-center"
                    )
                    if (isSaving) {
                        attr("disabled", "true")
                        classes("opacity-50", "cursor-not-allowed")
                    }
                    onClick { handleSave() }
                    title("Spremi")
                }) {
                    if (isSaving) I({ classes("fas", "fa-spinner", "fa-spin", "text-xl") })
                    else I({ classes("fas", "fa-save", "text-xl") })
                }
            }
        }

        if (isLoading) {
            Div({ classes("flex", "justify-center", "py-20") }) {
                I({ classes("fas", "fa-spinner", "fa-spin", "text-4xl", "text-emerald-500") })
            }
        } else {
            Div({ classes("space-y-8") }) {
                // Bosnian Translation
                SectionCard("Bosanski jezik", "fa-language") {
                    FormField("Ime") {
                        Input(InputType.Text) {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none"
                            )
                            value(bsName)
                            onInput { bsName = it.value }
                        }
                    }
                    FormField("Latinsko ime") {
                        Input(InputType.Text) {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none"
                            )
                            value(bsLatinName)
                            onInput { bsLatinName = it.value }
                        }
                    }
                    FormField("Opis") {
                        TextArea {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none",
                                "min-h-[100px]"
                            )
                            value(bsDescription)
                            onInput { bsDescription = it.value }
                        }
                    }
                }

                // English Translation
                SectionCard("Engleski jezik", "fa-language") {
                    FormField("Ime") {
                        Input(InputType.Text) {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none"
                            )
                            value(enName)
                            onInput { enName = it.value }
                        }
                    }
                    FormField("Latinsko ime") {
                        Input(InputType.Text) {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none"
                            )
                            value(enLatinName)
                            onInput { enLatinName = it.value }
                        }
                    }
                    FormField("Opis") {
                        TextArea {
                            classes(
                                "w-full",
                                "px-4",
                                "py-3",
                                "rounded-xl",
                                "border",
                                "border-gray-200",
                                "focus:ring-2",
                                "focus:ring-emerald-500",
                                "outline-none",
                                "min-h-[100px]"
                            )
                            value(enDescription)
                            onInput { enDescription = it.value }
                        }
                    }
                }

                // Parametri uzgoja
                SectionCard("Parametri uzgoja", "fa-seedling") {
                    Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-6") }) {
                        FormField("Razmak u redu (cm)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(spacingPlants)
                                onInput { spacingPlants = it.value?.toString() ?: "" }
                            }
                        }
                        FormField("Razmak između redova (cm)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(spacingRows)
                                onInput { spacingRows = it.value?.toString() ?: "" }
                            }
                        }
                        FormField("Visina biljke (cm)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(height)
                                onInput { height = it.value?.toString() ?: "" }
                            }
                        }
                        FormField("Ikona (Resource ID)") {
                            Input(InputType.Text) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(icon)
                                onInput { icon = it.value }
                            }
                        }
                    }

                    Div({ classes("space-y-4", "mt-6") }) {
                        RequirementSelector(
                            "Potrošnja resursa",
                            resourceConsumption
                        ) { resourceConsumption = it }
                        RequirementSelector(
                            "Potreba za vodom",
                            waterRequirements
                        ) { waterRequirements = it }
                        RequirementSelector(
                            "Osjetljivost na mraz",
                            frostSensitivity
                        ) { frostSensitivity = it }
                    }
                }

                // Temperatura
                SectionCard("Temperaturni uslovi", "fa-thermometer-half") {
                    Div({ classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-6") }) {
                        FormField("Min. Temp (°C)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(minTemp)
                                onInput { minTemp = it.value?.toString() ?: "" }
                            }
                        }
                        FormField("Optimalna Min (°C)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(optTempMin)
                                onInput { optTempMin = it.value?.toString() ?: "" }
                            }
                        }
                        FormField("Optimalna Max (°C)") {
                            Input(InputType.Number) {
                                classes(
                                    "w-full",
                                    "px-4",
                                    "py-3",
                                    "rounded-xl",
                                    "border",
                                    "border-gray-200",
                                    "focus:ring-2",
                                    "focus:ring-emerald-500",
                                    "outline-none"
                                )
                                value(optTempMax)
                                onInput { optTempMax = it.value?.toString() ?: "" }
                            }
                        }
                    }
                }

                // Raspored presađivanja
                SectionCard("Plan presađivanja", "fa-calendar-alt") {
                    TransplantScheduleSection(
                        "Kontinentalna regija",
                        contStart,
                        { contStart = it },
                        contEnd,
                        { contEnd = it },
                        contNotesBS,
                        contNotesEN,
                        { bs, en ->
                            contNotesBS = bs
                            contNotesEN = en
                        }
                    )
                    TransplantScheduleSection(
                        "Mediteranska regija",
                        medStart,
                        { medStart = it },
                        medEnd,
                        { medEnd = it },
                        medNotesBS,
                        medNotesEN,
                        { bs, en ->
                            medNotesBS = bs
                            medNotesEN = en
                        }
                    )
                    TransplantScheduleSection(
                        "Planinska regija",
                        mountStart,
                        { mountStart = it },
                        mountEnd,
                        { mountEnd = it },
                        mountNotesBS,
                        mountNotesEN,
                        { bs, en ->
                            mountNotesBS = bs
                            mountNotesEN = en
                        }
                    )
                }

                // Nutrijenti po fazama
                SectionCard("Faze nutrijenata", "fa-flask") {
                    nutrients.forEachIndexed { index, phase ->
                        NutrientStageCard(phase) { updated ->
                            nutrients = nutrients.toMutableList().apply { this[index] = updated }
                        }
                    }
                }

                // Companions
                SectionCard("Biljke pratilice", "fa-users") {
                    CompanionSelector("Dobre komšije", allCrops, selectedGoodCompanionIds) { id ->
                        selectedGoodCompanionIds =
                            if (selectedGoodCompanionIds.contains(id)) selectedGoodCompanionIds - id else selectedGoodCompanionIds + id
                    }
                    CompanionSelector("Loše komšije", allCrops, selectedBadCompanionIds) { id ->
                        selectedBadCompanionIds =
                            if (selectedBadCompanionIds.contains(id)) selectedBadCompanionIds - id else selectedBadCompanionIds + id
                    }
                }

                // Ostalo
                SectionCard("Dodatne opcije", "fa-plus-circle") {
                    Div({ classes("flex", "items-center", "gap-3", "cursor-pointer") }) {
                        Input(InputType.Checkbox) {
                            id("is-companion")
                            classes(
                                "w-5",
                                "h-5",
                                "rounded",
                                "border-gray-300",
                                "text-emerald-600",
                                "focus:ring-emerald-500"
                            )
                            checked(isCompanion)
                            onInput { isCompanion = it.value }
                        }
                        Label(
                            forId = "is-companion",
                            attrs = { classes("text-sm", "font-bold", "text-gray-700") }) {
                            Text("Može se koristiti kao biljka pratilica")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionCard(title: String, icon: String, content: @Composable () -> Unit) {
    Div({ classes("bg-white", "rounded-2xl", "border", "border-gray-100", "p-8", "shadow-sm") }) {
        Div({ classes("flex", "items-center", "gap-3", "mb-6") }) {
            Div({
                classes(
                    "w-10",
                    "h-10",
                    "rounded-lg",
                    "bg-emerald-50",
                    "flex",
                    "items-center",
                    "justify-center"
                )
            }) {
                I({ classes("fas", icon, "text-emerald-600") })
            }
            H2({ classes("text-xl", "font-bold", "text-gray-900") }) { Text(title) }
        }
        Div({ classes("space-y-6") }) {
            content()
        }
    }
}

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    Div {
        Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) {
            Text(
                label
            )
        }
        content()
    }
}

@Composable
private fun RequirementSelector(label: String, selectedValue: String, onSelect: (String) -> Unit) {
    Div {
        P({ classes("text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text(label) }
        Div({ classes("flex", "gap-3") }) {
            listOf(
                "low" to "Niska",
                "medium" to "Srednja",
                "high" to "Visoka"
            ).forEach { (value, text) ->
                val isSelected = selectedValue == value
                Button({
                    classes("px-4", "py-2", "rounded-lg", "border", "text-sm", "transition-all")
                    if (isSelected) {
                        classes("bg-emerald-600", "text-white", "border-emerald-600")
                    } else {
                        classes(
                            "bg-white",
                            "text-gray-600",
                            "border-gray-200",
                            "hover:border-emerald-500"
                        )
                    }
                    onClick { onSelect(value) }
                }) { Text(text) }
            }
        }
    }
}

@Composable
private fun CompanionSelector(
    label: String,
    allCrops: List<CropData>,
    selectedIds: Set<Int>,
    onToggle: (Int) -> Unit
) {
    Div {
        P({ classes("text-sm", "font-bold", "text-gray-700", "mb-3") }) { Text(label) }
        Div({ classes("flex", "flex-wrap", "gap-2") }) {
            allCrops.forEach { crop ->
                val isSelected = selectedIds.contains(crop.id)
                Div({
                    classes(
                        "flex",
                        "items-center",
                        "gap-2",
                        "px-3",
                        "py-1.5",
                        "rounded-full",
                        "border",
                        "text-sm",
                        "cursor-pointer",
                        "transition-all"
                    )
                    if (isSelected) {
                        classes("bg-emerald-100", "text-emerald-700", "border-emerald-200")
                    } else {
                        classes(
                            "bg-gray-50",
                            "text-gray-600",
                            "border-gray-100",
                            "hover:border-emerald-300"
                        )
                    }
                    onClick { onToggle(crop.id) }
                }) {
                    val imageUrl = getCropImageSource(crop.icon)
                    Img(
                        src = imageUrl
                            ?: "https://ui-avatars.com/api/?name=${crop.name}&background=ecfdf5&color=059669",
                        attrs = {
                            classes("w-4", "h-4", "object-contain", "rounded-full")
                            if (imageUrl != null) {
                                attr(
                                    "onerror",
                                    "this.src='https://ui-avatars.com/api/?name=${crop.name}&background=ecfdf5&color=059669'"
                                )
                            }
                        }
                    )
                    Text(crop.name)
                }
            }
        }
    }
}

@Composable
private fun TransplantScheduleSection(
    title: String,
    start: String, onStartChange: (String) -> Unit,
    end: String, onEndChange: (String) -> Unit,
    notesBS: String, notesEN: String, onNotesChange: (String, String) -> Unit
) {
    Div({
        classes(
            "border-b",
            "border-gray-100",
            "pb-6",
            "mb-6",
            "last:border-0",
            "last:pb-0",
            "last:mb-0"
        )
    }) {
        P({ classes("text-sm", "font-bold", "text-emerald-700", "mb-4") }) { Text(title) }
        Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-4", "mb-4") }) {
            FormField("Početni mjesec (1-12)") {
                Input(InputType.Number) {
                    classes(
                        "w-full",
                        "px-4",
                        "py-2",
                        "rounded-lg",
                        "border",
                        "border-gray-200",
                        "focus:ring-2",
                        "focus:ring-emerald-500",
                        "outline-none"
                    )
                    value(start)
                    onInput { onStartChange(it.value?.toString() ?: "") }
                }
            }
            FormField("Završni mjesec (1-12)") {
                Input(InputType.Number) {
                    classes(
                        "w-full",
                        "px-4",
                        "py-2",
                        "rounded-lg",
                        "border",
                        "border-gray-200",
                        "focus:ring-2",
                        "focus:ring-emerald-500",
                        "outline-none"
                    )
                    value(end)
                    onInput { onEndChange(it.value?.toString() ?: "") }
                }
            }
        }
        FormField("Napomene za regiju BS") {
            Input(InputType.Text) {
                classes(
                    "w-full",
                    "px-4",
                    "py-2",
                    "rounded-lg",
                    "border",
                    "border-gray-200",
                    "focus:ring-2",
                    "focus:ring-emerald-500",
                    "outline-none"
                )
                value(notesBS)
                onInput { onNotesChange(it.value, notesEN) }
            }
        }
        FormField("Napomene za regiju EN") {
            Input(InputType.Text) {
                classes(
                    "w-full",
                    "px-4",
                    "py-2",
                    "rounded-lg",
                    "border",
                    "border-gray-200",
                    "focus:ring-2",
                    "focus:ring-emerald-500",
                    "outline-none"
                )
                value(notesEN)
                onInput { onNotesChange(notesBS, it.value) }
            }
        }
    }
}

@Composable
private fun NutrientStageCard(stage: NutrientStage, onChange: (NutrientStage) -> Unit) {
    Div({ classes("bg-gray-50", "rounded-xl", "p-6", "mb-4") }) {
        Div({ classes("grid", "grid-cols-1", "gap-4") }) {
            val displayName = when (stage.phaseName?.lowercase()) {
                "transplanting" -> "Presađivanje"
                "flowering" -> "Cvjetanje"
                "fruiting" -> "Plodonošenje"
                else -> stage.phaseName ?: ""
            }

            H3({ classes("font-bold", "text-emerald-800", "mb-2") }) { Text(displayName) }

            Div({ classes("grid", "grid-cols-2", "gap-4") }) {
                FormField("Početna sedmica") {
                    Input(InputType.Number) {
                        classes(
                            "w-full",
                            "px-4",
                            "py-2",
                            "rounded-lg",
                            "border",
                            "border-gray-200",
                            "focus:ring-2",
                            "focus:ring-emerald-500",
                            "outline-none"
                        )
                        value(stage.weekStart?.toString() ?: "")
                        onInput {
                            val valStr = it.value?.toString() ?: ""
                            onChange(stage.copy(weekStart = valStr.toIntOrNull()))
                        }
                    }
                }
                FormField("Završna sedmica") {
                    Input(InputType.Number) {
                        classes(
                            "w-full",
                            "px-4",
                            "py-2",
                            "rounded-lg",
                            "border",
                            "border-gray-200",
                            "focus:ring-2",
                            "focus:ring-emerald-500",
                            "outline-none"
                        )
                        value(stage.weekEnd?.toString() ?: "")
                        onInput {
                            val valStr = it.value?.toString() ?: ""
                            onChange(stage.copy(weekEnd = valStr.toIntOrNull()))
                        }
                    }
                }
            }
            Div({ classes("grid", "grid-cols-3", "gap-4") }) {
                FormField("N (Dušik)") {
                    Input(InputType.Text) {
                        classes(
                            "w-full",
                            "px-4",
                            "py-2",
                            "rounded-lg",
                            "border",
                            "border-gray-200",
                            "focus:ring-2",
                            "focus:ring-emerald-500",
                            "outline-none"
                        )
                        value(stage.nitrogen ?: "")
                        onInput { onChange(stage.copy(nitrogen = it.value)) }
                    }
                }
                FormField("P (Fosfor)") {
                    Input(InputType.Text) {
                        classes(
                            "w-full",
                            "px-4",
                            "py-2",
                            "rounded-lg",
                            "border",
                            "border-gray-200",
                            "focus:ring-2",
                            "focus:ring-emerald-500",
                            "outline-none"
                        )
                        value(stage.phosphorus ?: "")
                        onInput { onChange(stage.copy(phosphorus = it.value)) }
                    }
                }
                FormField("K (Kalijum)") {
                    Input(InputType.Text) {
                        classes(
                            "w-full",
                            "px-4",
                            "py-2",
                            "rounded-lg",
                            "border",
                            "border-gray-200",
                            "focus:ring-2",
                            "focus:ring-emerald-500",
                            "outline-none"
                        )
                        value(stage.potassium ?: "")
                        onInput { onChange(stage.copy(potassium = it.value)) }
                    }
                }
            }
        }
    }
}
